import parse, {
  attributesToProps,
  domToReact,
  type DOMNode,
  type Element as DOMElement,
  type HTMLReactParserOptions,
} from "html-react-parser";
import { useMemo } from "react";
import { AnnotationDetails } from "~/components/build/Show/components/annotations/AnnotationDetails";

interface Props {
  /** Stable id for the annotation. Used to namespace per-element state in the store. */
  annotationId: string;
  /** Server-rendered annotation body HTML. May be empty. */
  html: string | null | undefined;
}

/**
 * Renders an annotation body by parsing the server HTML into React. Stateful elements
 * (currently just `<details>`) are swapped for controlled React components whose state lives
 * in `useAnnotationElementStateStore`. Everything else falls through to default rendering.
 *
 * Why parse instead of `dangerouslySetInnerHTML`: HTML-level changes (e.g. fresh AWS
 * presigned URLs on each refetch) only update the affected props via React reconciliation,
 * rather than blasting `setInnerHTML` on the wrapper and wiping browser-managed state from
 * every descendant.
 */
export function AnnotationBody({ annotationId, html }: Props) {
  const parsed = useMemo(() => {
    if (!html) {
      return null;
    }

    // Counters give us stable per-tag-type paths (e.g. `details[0]`, `details[1]`) that
    // survive minor body churn so long as the structural position of the element doesn't
    // shift. Same idea works for any other stateful element we add later.
    const counters: Record<string, number> = {};

    const nextPath = (tagName: string) => {
      const index = counters[tagName] ?? 0;
      counters[tagName] = index + 1;
      return `${tagName}[${index}]`;
    };

    const options: HTMLReactParserOptions = {
      replace: (node) => {
        if (node.type !== "tag") {
          return undefined;
        }
        const el = node as DOMElement;

        if (el.name === "details") {
          const path = nextPath("details");
          // Strip `open` from passthrough attrs — `AnnotationDetails` owns that prop. All
          // other attributes (`id`, `class`, `data-*`, etc.) are preserved so authored
          // annotation markup keeps its semantics/styling/anchor targets.
          const { open: _open, ...restAttribs } = el.attribs;
          const props = attributesToProps(restAttribs);
          return (
            <AnnotationDetails
              {...props}
              annotationId={annotationId}
              path={path}
              initialOpen={"open" in el.attribs}
            >
              {domToReact(el.children as DOMNode[], options)}
            </AnnotationDetails>
          );
        }

        return undefined;
      },
    };

    return parse(html, options);
  }, [annotationId, html]);

  return <div className="annotation-body ph-no-capture m-4">{parsed}</div>;
}
